import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { InvoiceModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/invoice.model';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { downloadInvoicePdf } from '@app/feature/invoice/helper/invoice-pdf.helper';
import { InvoiceDetailsLoadingView } from '@app/feature/invoice/invoice-details/invoice-details-loading.view';
import { InvoiceDetailsView } from '@app/feature/invoice/invoice-details/invoice-details.view';
import { InvoiceContextProvider } from '@app/feature/invoice/invoice.context';
import { useSetPageTitleContext } from '@app/shared/layout/page-layout/page-layout-title.context-provider';
import {
  useCpmsBoGetInvoiceQuery,
  useLazyCpmsBoGetInvoicePdfQuery,
} from '@service/cpms-bo-invoice.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';

interface InvoiceDetailsControllerProps {
  resource: InvoiceModel;
}

const InvoiceDetailsController = (
  props: InvoiceDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [lazyGetInvoicePdfQuery] = useLazyCpmsBoGetInvoicePdfQuery();

  const handleDownloadInvoicePdf = useCallback(async () => {
    try {
      const { isError, isSuccess, data, error } = await lazyGetInvoicePdfQuery(
        resource.id,
      );

      if (isError) {
        throw new Error(JSON.stringify(error));
      }

      if (isSuccess) {
        await downloadInvoicePdf(resource, data);
        toast.success(t('$.invoice-download-pdf.success'));
      }
    } catch (error: any) {
      toast.error(
        t('$.invoice-download-pdf.error', {
          error: error.toString(),
        }),
      );
    }
  }, [lazyGetInvoicePdfQuery, resource, t]);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.invoiceNumber);
  }, [resource.invoiceNumber, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.INVOICES,
        action: ActionsByResource.INVOICES.GET_INVOICE_PDF,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="download-pdf"
          onClick={handleDownloadInvoicePdf}
          icon="download-01"
        />,
      );
    }

    setPageAction(actions);
  }, [handleDownloadInvoicePdf, setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <InvoiceContextProvider data={resource}>
      <InvoiceDetailsView />
    </InvoiceContextProvider>
  );
};

export const InvoiceDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetInvoiceQuery(params.id ?? '');

  const { Component } = withProtectedResource<InvoiceModel, {}, InvoiceModel>(
    InvoiceDetailsController,
    queryState,
    {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.INVOICES,
          action: ActionsByResource.INVOICES.READ,
        }),
      loadingComponent: <InvoiceDetailsLoadingView />,
    },
  )({});

  return Component;
};
